// @flow
/** @jsx jsx */

import {
  type ComponentType,
  Fragment,
  type Node,
  useEffect,
  useRef,
  useState,
} from 'react';

import Dropdown from '../Dropdown';
import { Colors } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import { jsx } from '@emotion/core';

import MenuItem from './MenuItem';
import Toggle from './Toggle';

import type {
  HorizontalType,
  VerticalType,
} from '../Dropdown/Dropdown';

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
type Item = *;

type Props = {
  children: Node,
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `value` property on each item object.
   */
  getKeyForItem?: (item: Item) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item, isConfirming: ?boolean) => string,
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean,
  /** Disables entire input */
  isDisabled?: boolean,
  /**
   * The list of items. Each item gets passed as props to `MenuItemComponent` to be rendered.
   */
  items: Array<Item>,
  /** Label that goes in Toggle */
  label?: string,
  /**
   * The Component to use for rendering each item in the list of `items`. Defaults to an internal implementation.
   */
  MenuItemComponent?: ComponentType<*>,
  /** a11y label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc. */
  menuAriaLabel?: string,
  /** Menu Horizontal Position */
  menuHorizontal?: HorizontalType,
  /** Menu is full width? */
  menuIsBlock?: boolean,
  /** Menu maximum height. */
  menuMaxHeight?: string,
  /** Menu minimum width. */
  menuMinWidth?: string,
  /** Menu Vertical Position */
  menuVertical?: VerticalType,
  /**
   * Wrap each item or not.
   * Defaults to false.
   * */
  nowrap: boolean,
  /** Callback that gets called whenever the dropdown is opened */
  onOpen?: (Array<Item>) => void,
  /** Callback that gets called when the user selects a menu item. */
  onSelect: (item: Item) => void,
  /**
   * Render dividers between items to separate them.
   * Defaults to false.
   * */
  showDividers: boolean,
  /** Customizes which HTML tag gets rendered as the dropdown */
  tagDropdown: string,
  /** Customizes which HTML tag gets rendered as the menu item */
  tagMenuItem: string,
  /**
   * The Component used for rendering the menu toggle
   */
  ToggleComponent: ComponentType<*>,
};

const defaultProps = {
  getLabelForItem: (item: Item, isConfirming: ?boolean) =>
    isConfirming ? 'Really?' : item.label,
  isDisabled: false,
  isLoading: false,
  isItemDisabled: (item: Item) => item.isDisabled,
  nowrap: false,
  showDividers: false,
  tagDropdown: 'ul',
  tagMenuItem: 'li',
  ToggleComponent: Toggle,
};

const outsideToggle = {
  cursor: 'pointer',

  '&[disabled]': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
};

const menuCss = {
  cursor: 'pointer',
  display: 'block',
  position: 'relative',
};

const dividerCss = {
  border: 'none',
  height: 1,
  margin: 0,
  flexShrink: 0,
  backgroundColor: Colors.adamantium,
};

const DropdownMenu = (props: Props) => {
  const { items, onOpen, showDividers } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const menuRef = useRef();
  const toggleRef = useRef();

  const hasMenuItems = Array.isArray(items) && items.length > 0;
  const isDisabled = !hasMenuItems || props.isDisabled;

  const handleClick = () => {
    if (isOpen) {
      setFocusedIndex(-1);
      // $FlowFixMe
      toggleRef.current.focus();
    }
    setIsOpen(!isOpen);
  };

  // Handle toggle key down
  const handleKeyDown = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.key === 'ArrowDown') {
      if (!isOpen) {
        setIsOpen(true);
      }
      // focus first menu item
      setFocusedIndex(0);
    } else {
      handleClick();
    }
  };

  // Call onOpen when menu opens (if exists)
  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen(items);
    }
  }, [isOpen, items, onOpen]);

  // Handle document mouse events (for outside click)
  useEffect(() => {
    // Handle outside clicking
    const handleDocClick = e => {
      // $FlowFixMe - flow doesn't understand useRef
      if (!menuRef.current.contains(e.target)) {
        // Close menu when use has clicked outside dropdown
        setIsOpen(false);
      }
    };

    // add when mounted
    // $FlowFixMe
    document.addEventListener('mousedown', handleDocClick);
    return () => {
      // handle unmount
      // $FlowFixMe
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, []);

  const onKeyDown = isDisabled
    ? null
    : createKeyHandler(['space', 'down', 'enter'], handleKeyDown);

  return (
    <div disabled={isDisabled} open={isOpen} ref={menuRef}>
      <div
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="menu"
        css={outsideToggle}
        disabled={isDisabled}
        onClick={isDisabled ? null : handleClick}
        onKeyDown={onKeyDown}
        ref={toggleRef}
        role="button"
        tabIndex={0}
      >
        <props.ToggleComponent
          isDisabled={isDisabled}
          isOpen={isOpen}
          label={props.label}
        />
      </div>
      {isOpen && (
        <div aria-label={props.menuAriaLabel} css={menuCss}>
          <Dropdown
            horizontal={props.menuHorizontal}
            isBlock={props.menuIsBlock}
            maxHeight={props.menuMaxHeight}
            minWidth={props.menuMinWidth}
            role="menu"
            tag={props.tagDropdown}
            vertical={props.menuVertical}
          >
            {!isDisabled &&
              items.map((item: Item, index: number) => (
                <Fragment
                  key={props.getKeyForItem ? props.getKeyForItem(item) : index}
                >
                  <MenuItem
                    getLabelForItem={props.getLabelForItem}
                    index={index}
                    isItemDisabled={props.isItemDisabled}
                    item={item}
                    MenuItemComponent={props.MenuItemComponent}
                    nowrap={props.nowrap}
                    onClick={() => {
                      handleClick();
                      props.onSelect(item);
                    }}
                    onKeyDown={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (e.key === 'Enter') {
                        // Select Item
                        handleClick();
                        props.onSelect(item);
                      } else if (e.key === 'ArrowUp') {
                        // Focus next item
                        setFocusedIndex(
                          (focusedIndex - 1 + items.length) % items.length
                        );
                      } else if (e.key === 'ArrowDown') {
                        // Focus prev item
                        setFocusedIndex((focusedIndex + 1) % items.length);
                      } else {
                        // Space - close menu
                        handleClick();
                      }
                    }}
                    shouldFocus={index === focusedIndex}
                    tag={props.tagMenuItem}
                  />
                  {showDividers && index !== items.length - 1 && (
                    <hr css={dividerCss} />
                  )}
                </Fragment>
              ))}
          </Dropdown>
        </div>
      )}
    </div>
  );
};

DropdownMenu.defaultProps = defaultProps;

export default DropdownMenu;
